import React, { useState, useMemo, useCallback } from 'react';
import { Table as TableComponent } from '@lucid/lego';
import type { CustomerCase } from '../../../../api/services/lupp/customer/dto.customer';
import { createColumns, type Case } from './utils';
import Details from './Details/Details';

interface TableProps {
  theme: 'light' | 'dark';
  data: CustomerCase[];
  totalCount?: number;
  currentPage?: number;
  pageSize?: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  searchQuery: string;
  onSearchChange: (value: string) => void;
}

const Table: React.FC<TableProps> = ({
  theme,
  data,
  totalCount,
  currentPage,
  pageSize = 10,
  onPageChange,
  onPageSizeChange,
  searchQuery,
  onSearchChange,
}) => {
  const [isSliderOpen, setIsSliderOpen] = useState(false);
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null);

  const handleEditClick = useCallback((record: Case) => {
    setSelectedCaseId(record.customerCaseIdentifier || String(record.caseIdentifier || ''));
    setIsSliderOpen(true);
  }, []);

  const columns = useMemo(
    () => createColumns({ theme, onEditClick: handleEditClick }),
    [theme, handleEditClick]
  );

  return (
    <>
      <TableComponent
        columns={columns as never}
        data={data as never[]}
        theme={theme === 'light' ? 'dark' : 'light'}
        pagination={{
          enabled: true,
          total: totalCount,
          current: currentPage,
          pageSize,
          onChange: (page) => onPageChange(page),
          onPageSizeChange,
        }}
        search={{
          placeholder: 'Search cases by number, name, VIN, or status...',
          value: searchQuery,
          onChange: onSearchChange,
        }}
        columnConfig={{ enabled: true }}
        selection={{ enabled: false }}
        emptyState={{
          message: searchQuery.trim()
            ? 'No results found! Please update your query'
            : 'No cases available',
        }}
        gaComponent="case-tracker-table"
        dataTestId="case-tracker-table"
      />
      <Details
        theme={theme}
        isOpen={isSliderOpen}
        onClose={() => {
          setIsSliderOpen(false);
          setSelectedCaseId(null);
        }}
        caseId={selectedCaseId || ''}
      />
    </>
  );
};

export default Table;
